/**
 * Per-project Dashboard sub-tabs
 *
 * Renders 9 data-rich sub-tabs inside the Project Detail page:
 *   MPR · Staff/Labour · Material Received · WDQ · Machinery ·
 *   Hindrance · Weather · Safety · RFI
 *
 * Each tab is a small functional component that loads its slice from
 * /api/projects/{id}/dashboard/{slug} and renders a stats strip + table.
 * Hindrance / Weather / Safety also expose a "Quick Log" form.
 *
 * The whole module is one file so ProjectDetail.jsx stays uncluttered.
 */
import React, { useEffect, useState, useCallback } from 'react';
import {
  Package, Users, Truck, BarChart3, Wrench, AlertTriangle, CloudRain,
  Shield, HelpCircle, RefreshCw, Plus, X, CheckCircle2,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { apiGet, apiPost } from '../../lib/api';
import { formatDate as fmtDate } from '../../lib/format';

const inr = (v) => '₹' + Number(v || 0).toLocaleString('en-IN');

// ─────────────────────────────────────────────────────────────
// Shared primitives
// ─────────────────────────────────────────────────────────────
const StatCard = ({ label, value, sub }) => (
  <div className="bg-white border border-slate-200 rounded-sm p-3">
    <div className="text-[10px] uppercase tracking-widest font-display text-slate-500">{label}</div>
    <div className="text-2xl font-display font-bold mt-1 tabular-nums">{value}</div>
    {sub && <div className="text-[11px] text-slate-400 mt-0.5">{sub}</div>}
  </div>
);

const EmptyState = ({ icon: Icon, label, hint }) => (
  <div className="text-center py-10 text-slate-400">
    <Icon size={36} className="mx-auto mb-2 opacity-40" />
    <div className="text-sm font-display font-semibold">{label}</div>
    {hint && <div className="text-xs mt-1">{hint}</div>}
  </div>
);

const SectionHeader = ({ title, onRefresh, extra }) => (
  <div className="flex items-center justify-between mb-3">
    <h3 className="font-display font-bold text-base text-[#0F172A]">{title}</h3>
    <div className="flex items-center gap-2">
      {extra}
      {onRefresh && (
        <button
          onClick={onRefresh}
          className="px-2 py-1 text-xs text-slate-500 hover:text-[#3b4872] flex items-center gap-1"
          data-testid="pd-refresh"
        >
          <RefreshCw size={12} /> Refresh
        </button>
      )}
    </div>
  </div>
);

function useTabData(projectId, slug) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await apiGet(`/projects/${projectId}/dashboard/${slug}`);
      setData(d);
    } catch (e) {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [projectId, slug]);
  useEffect(() => { load(); }, [load]);
  return { data, loading, reload: load };
}

// ─────────────────────────────────────────────────────────────
// 1. MPR
// ─────────────────────────────────────────────────────────────
export function MPRTab({ project }) {
  const { data, loading, reload } = useTabData(project.id, 'mpr');
  const items = data?.items || [];
  const s = data?.stats || {};
  return (
    <div className="space-y-4" data-testid="pd-mpr">
      <SectionHeader title="Material Procurement Requests" onRefresh={reload}
        extra={<Link to="/app/procurement" className="text-xs text-[#3b4872] hover:underline">Open Procurement →</Link>} />
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatCard label="Total" value={s.total ?? 0} />
        <StatCard label="Draft" value={s.draft ?? 0} />
        <StatCard label="RFQ Issued" value={s.rfq_issued ?? 0} />
        <StatCard label="Approved" value={s.approved ?? 0} />
        <StatCard label="Est. Value" value={inr(s.value)} />
      </div>
      {loading ? <div className="text-sm text-slate-400 italic">Loading…</div> :
        items.length === 0 ? <EmptyState icon={Package} label="No MPRs raised yet" hint="Create one from Procurement → New MPR" /> :
        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
          <table className="w-full text-xs"><thead className="bg-slate-50">
            <tr><th className="text-left px-3 py-2">MPR No</th><th className="text-left px-3 py-2">Item</th><th className="text-right px-3 py-2">Qty</th><th className="text-right px-3 py-2">Value</th><th className="text-left px-3 py-2">Status</th><th className="text-left px-3 py-2">Date</th></tr>
          </thead><tbody>{items.map(m => (
            <tr key={m.id} className="border-t border-slate-100 hover:bg-slate-50">
              <td className="px-3 py-2 font-mono text-[11px]">{m.mpr_no || m.id?.slice(0, 8)}</td>
              <td className="px-3 py-2">{m.item || (Array.isArray(m.items) ? `${m.items.length} items` : '—')}</td>
              <td className="px-3 py-2 text-right tabular-nums">{m.quantity || (m.items?.reduce((s,i)=>s+(i.quantity||0),0)) || 0} {m.unit || ''}</td>
              <td className="px-3 py-2 text-right tabular-nums">{inr(m.estimated_value)}</td>
              <td className="px-3 py-2"><span className="px-1.5 py-0.5 text-[10px] rounded-sm bg-slate-100">{m.status || 'Draft'}</span></td>
              <td className="px-3 py-2 text-slate-500">{(m.created_at || '').slice(0,10)}</td>
            </tr>))}</tbody></table>
        </div>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// 2. Staff / Labour
// ─────────────────────────────────────────────────────────────
export function LabourTab({ project }) {
  const { data, loading, reload } = useTabData(project.id, 'labour');
  const s = data?.stats || {};
  const today = data?.today_rows || [];
  const timeline = data?.timeline || [];
  // Automation #8 — today's mandays vs trailing 7-day average per crew.
  const [mdAlerts, setMdAlerts] = useState([]);
  useEffect(() => {
    apiGet('/automation/mandays-drop', { threshold_pct: 25 })
      .then((r) => setMdAlerts((r.alerts || []).filter((a) => a.project_id === project.id)))
      .catch(() => {});
  }, [project.id]);
  return (
    <div className="space-y-4" data-testid="pd-labour">
      <SectionHeader title="Staff & Labour Attendance" onRefresh={reload}
        extra={<Link to="/app/labour" className="text-xs text-[#3b4872] hover:underline">Open Labour Master →</Link>} />
      {mdAlerts.length > 0 && (
        <div className="border border-amber-300 bg-amber-50 rounded-sm p-3 text-xs text-amber-800" data-testid="pd-labour-mandays-alert">
          <div className="font-display font-bold flex items-center gap-1.5 mb-1"><AlertTriangle size={13} /> Mandays drop alert — today vs trailing 7-day average</div>
          {mdAlerts.map((a, i) => (
            <div key={i}>{a.subcontractor_name || 'Crew'}{a.trade ? ` · ${a.trade}` : ''}: {a.today_mandays} mandays today vs {a.trailing_avg} avg — down {a.drop_pct}%</div>
          ))}
        </div>
      )}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Present Today" value={s.today_present ?? 0} />
        <StatCard label="Absent Today" value={s.today_absent ?? 0} />
        <StatCard label="Active Labour" value={s.active_labour_master ?? 0} />
        <StatCard label="30-day Mandays" value={s['30d_total_mandays'] ?? 0} />
      </div>
      {loading ? <div className="text-sm text-slate-400 italic">Loading…</div> : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <div className="bg-white border border-slate-200 rounded-sm p-3">
            <div className="text-xs font-display font-semibold text-slate-700 mb-2">Last 30 Days</div>
            {timeline.length === 0 ? <EmptyState icon={Users} label="No attendance recorded" hint="Mark attendance from /labour" />
              : <table className="w-full text-xs"><thead><tr className="text-slate-500"><th className="text-left">Date</th><th className="text-right">Present</th><th className="text-right">Absent</th><th className="text-right">Skilled</th></tr></thead>
                <tbody>{timeline.map(r=>(<tr key={r.date} className="border-t border-slate-100"><td>{r.date}</td><td className="text-right">{r.present}</td><td className="text-right">{r.absent}</td><td className="text-right">{r.skilled}</td></tr>))}</tbody></table>}
          </div>
          <div className="bg-white border border-slate-200 rounded-sm p-3">
            <div className="text-xs font-display font-semibold text-slate-700 mb-2">Today's Roster</div>
            {today.length === 0 ? <EmptyState icon={Users} label="No one marked today" />
              : <ul className="text-xs space-y-1">{today.slice(0,30).map((r,i)=>(<li key={i} className="flex justify-between border-b border-slate-100 py-1"><span>{r.name || r.labour_id}</span><span className={(r.status || '').toLowerCase()==='present' ? 'text-emerald-600' : 'text-slate-400'}>{r.status}</span></li>))}</ul>}
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// 3. Material Received
// ─────────────────────────────────────────────────────────────
export function MaterialReceivedTab({ project }) {
  const { data, loading, reload } = useTabData(project.id, 'material-received');
  const items = data?.items || [];
  const s = data?.stats || {};
  return (
    <div className="space-y-4" data-testid="pd-material">
      <SectionHeader title="Material Received (GRN)" onRefresh={reload}
        extra={<Link to="/app/stores" className="text-xs text-[#3b4872] hover:underline">Open Stores →</Link>} />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Total GRNs" value={s.total ?? 0} />
        <StatCard label="Value Received" value={inr(s.value)} />
        <StatCard label="Pending QC" value={s.pending_qc ?? 0} />
        <StatCard label="Rejected" value={s.rejected ?? 0} />
      </div>
      {loading ? <div className="text-sm text-slate-400 italic">Loading…</div> :
        items.length === 0 ? <EmptyState icon={Truck} label="No materials received yet" hint="Receive against a PO in Stores → New GRN" /> :
        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
          <table className="w-full text-xs"><thead className="bg-slate-50">
            <tr><th className="text-left px-3 py-2">GRN</th><th className="text-left px-3 py-2">PO / Vendor</th><th className="text-right px-3 py-2">Items</th><th className="text-right px-3 py-2">Value</th><th className="text-left px-3 py-2">QC</th><th className="text-left px-3 py-2">Date</th><th className="text-left px-3 py-2">Source</th></tr>
          </thead><tbody>{items.map((g,i) => (
            <tr key={g.id || i} className="border-t border-slate-100">
              <td className="px-3 py-2 font-mono text-[11px]">{g.grn_no || g.id?.slice(0,8)}</td>
              <td className="px-3 py-2">{g.vendor_name || g.po_id || '—'}</td>
              <td className="px-3 py-2 text-right tabular-nums">{(g.items || []).length || '—'}</td>
              <td className="px-3 py-2 text-right tabular-nums">{inr(g.total_amount || g.total_value)}</td>
              <td className="px-3 py-2"><span className="px-1.5 py-0.5 text-[10px] rounded-sm bg-slate-100">{g.qc_status || '—'}</span></td>
              <td className="px-3 py-2 text-slate-500">{(g.received_date || g.created_at || '').slice(0,10)}</td>
              <td className="px-3 py-2 text-[10px] text-slate-400">{g._source}</td>
            </tr>))}</tbody></table>
        </div>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// 4. WDQ
// ─────────────────────────────────────────────────────────────
export function WDQTab({ project }) {
  const { data, loading, reload } = useTabData(project.id, 'wdq');
  const items = data?.items || [];
  const s = data?.stats || {};
  return (
    <div className="space-y-4" data-testid="pd-wdq">
      <SectionHeader title="Work Done Quantity (BOQ-wise)" onRefresh={reload}
        extra={<Link to={`/app/projects/${project.id}`} className="text-xs text-[#3b4872] hover:underline">Open BOQ →</Link>} />
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatCard label="BOQ Items" value={s.items ?? 0} />
        <StatCard label="Completed" value={s.completed ?? 0} />
        <StatCard label="In Progress" value={s.in_progress ?? 0} />
        <StatCard label="Not Started" value={s.not_started ?? 0} />
        <StatCard label="Value Done" value={inr(s.total_value_done)} />
      </div>
      {loading ? <div className="text-sm text-slate-400 italic">Loading…</div> :
        items.length === 0 ? <EmptyState icon={BarChart3} label="No BOQ items yet" hint="Import BOQ on the BOQ & Schedule tab" /> :
        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
          <table className="w-full text-xs"><thead className="bg-slate-50">
            <tr><th className="text-left px-3 py-2">Code</th><th className="text-left px-3 py-2">Description</th><th className="text-right px-3 py-2">Planned</th><th className="text-right px-3 py-2">Done</th><th className="text-right px-3 py-2">Balance</th><th className="text-right px-3 py-2">%</th><th className="text-right px-3 py-2">Value Done</th></tr>
          </thead><tbody>{items.map((it, i) => (
            <tr key={i} className="border-t border-slate-100">
              <td className="px-3 py-2 font-mono text-[11px]">{it.item_code || '—'}</td>
              <td className="px-3 py-2 max-w-[300px] truncate">{it.description}</td>
              <td className="px-3 py-2 text-right tabular-nums">{it.planned?.toFixed(1)} {it.unit}</td>
              <td className="px-3 py-2 text-right tabular-nums text-emerald-700">{it.done?.toFixed(1)}</td>
              <td className="px-3 py-2 text-right tabular-nums text-slate-500">{it.balance?.toFixed(1)}</td>
              <td className="px-3 py-2 text-right">
                <div className="inline-block w-16">
                  <div className="bg-slate-100 rounded-full h-1.5 overflow-hidden"><div className="h-full bg-[#3b4872]" style={{width: `${Math.min(100, it.progress_pct)}%`}} /></div>
                  <div className="text-[10px] mt-0.5 text-slate-500">{it.progress_pct}%</div>
                </div>
              </td>
              <td className="px-3 py-2 text-right tabular-nums">{inr(it.value_done)}</td>
            </tr>))}</tbody></table>
        </div>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// 5. Machinery
// ─────────────────────────────────────────────────────────────
export function MachineryTab({ project }) {
  const { data, loading, reload } = useTabData(project.id, 'machinery');
  const items = data?.items || [];
  const s = data?.stats || {};
  return (
    <div className="space-y-4" data-testid="pd-machinery">
      <SectionHeader title="Machinery & Equipment" onRefresh={reload}
        extra={<Link to="/app/fuel/equipment" className="text-xs text-[#3b4872] hover:underline">Open Equipment Master →</Link>} />
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatCard label="Total Equipment" value={s.total_equipment ?? 0} />
        <StatCard label="Active" value={s.active ?? 0} />
        <StatCard label="Breakdown" value={s.breakdown ?? 0} />
        <StatCard label="30d Fuel L" value={s.litres_30d ?? 0} />
        <StatCard label="30d Fuel ₹" value={inr(s.amount_30d)} />
      </div>
      {loading ? <div className="text-sm text-slate-400 italic">Loading…</div> :
        items.length === 0 ? <EmptyState icon={Wrench} label="No equipment assigned" hint="Add equipment from Fuel → Equipment Master" /> :
        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
          <table className="w-full text-xs"><thead className="bg-slate-50">
            <tr><th className="text-left px-3 py-2">Equipment</th><th className="text-left px-3 py-2">Type</th><th className="text-left px-3 py-2">Model</th><th className="text-left px-3 py-2">Operator</th><th className="text-left px-3 py-2">Status</th><th className="text-right px-3 py-2">30d L</th><th className="text-right px-3 py-2">30d ₹</th></tr>
          </thead><tbody>{items.map(e => (
            <tr key={e.id} className="border-t border-slate-100">
              <td className="px-3 py-2 font-mono text-[11px]">{e.equipment_id}</td>
              <td className="px-3 py-2">{e.type}</td>
              <td className="px-3 py-2">{e.model}</td>
              <td className="px-3 py-2">{e.operator || '—'}</td>
              <td className="px-3 py-2"><span className={`px-1.5 py-0.5 text-[10px] rounded-sm ${e.status==='active'?'bg-emerald-50 text-emerald-700':'bg-slate-100'}`}>{e.status}</span></td>
              <td className="px-3 py-2 text-right tabular-nums">{e.litres_30d?.toFixed(1)}</td>
              <td className="px-3 py-2 text-right tabular-nums">{inr(e.amount_30d)}</td>
            </tr>))}</tbody></table>
        </div>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// 6. Hindrance — repointed to the real Hindrance Register (db.hindrances),
//    same columns/actions as HindranceRegister.jsx (Item C fix — this used
//    to read the separate, EOT-blind `hindrance_logs` quick-log collection).
// ─────────────────────────────────────────────────────────────
const RESP = ['Client', 'Contractor', 'Subcontractor', 'Other'];
const RESP_TONE = {
  Client: 'bg-blue-50 text-blue-700 border-blue-200',
  Contractor: 'bg-purple-50 text-purple-700 border-purple-200',
  Subcontractor: 'bg-orange-50 text-orange-700 border-orange-200',
  Other: 'bg-slate-100 text-slate-600 border-slate-200',
};
const STATUS_TONE = {
  open: 'bg-red-50 text-red-700 border-red-200',
  in_progress: 'bg-amber-50 text-amber-700 border-amber-200',
  resolved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  waived: 'bg-slate-100 text-slate-600 border-slate-200',
};
export function HindranceTab({ project }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', responsibility: 'Client', impact_days: '', expected_resolution_date: '' });

  const reload = useCallback(() => {
    setLoading(true);
    apiGet(`/hindrances?project_id=${project.id}`).then(setData).catch(() => toast.error('Failed to load hindrances')).finally(() => setLoading(false));
  }, [project.id]);
  useEffect(reload, [reload]);

  const submit = () => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    apiPost('/hindrances', { ...form, project_id: project.id, impact_days: Number(form.impact_days || 0) })
      .then(() => { toast.success('Hindrance logged'); setShowForm(false); setForm({ title: '', description: '', responsibility: 'Client', impact_days: '', expected_resolution_date: '' }); reload(); })
      .catch((e) => toast.error(e?.response?.data?.detail || 'Failed to create'));
  };
  const resolve = (h) => {
    apiPost(`/hindrances/${h.id}/resolve`, { status: 'resolved' })
      .then(() => { toast.success('Marked resolved'); reload(); })
      .catch(() => toast.error('Failed to resolve'));
  };

  const rows = data?.hindrances || [];
  return (
    <div className="space-y-4" data-testid="pd-hindrance">
      <SectionHeader title="Hindrance Register" onRefresh={reload}
        extra={<button onClick={() => setShowForm(true)} className="px-2 py-1 text-xs bg-[#3b4872] text-white hover:bg-[#2d3856] rounded-sm flex items-center gap-1" data-testid="pd-hindrance-add"><Plus size={12} /> Log Hindrance</button>} />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Total" value={data?.count ?? 0} />
        <StatCard label="Open" value={data?.open_count ?? 0} />
        <StatCard label="Total Days Lost" value={(data?.total_days_lost ?? 0).toFixed(1)} />
        <StatCard label="By Client" value={(data?.days_lost_by_party?.Client ?? 0).toFixed(1)} sub="days" />
      </div>
      {showForm && (
        <div className="bg-white border border-slate-200 rounded-sm p-4 space-y-3" data-testid="pd-hindrance-form">
          <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Title (e.g. Drawing approval delayed by consultant)" className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" data-testid="pd-hindrance-title-input" />
          <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Description / context" rows={2} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
          <div className="grid grid-cols-3 gap-3">
            <select value={form.responsibility} onChange={e => setForm({ ...form, responsibility: e.target.value })} className="px-3 py-2 border border-slate-300 rounded-sm text-sm" data-testid="pd-hindrance-resp-select">
              {RESP.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
            <input type="number" value={form.impact_days} onChange={e => setForm({ ...form, impact_days: e.target.value })} placeholder="Impact (days)" className="px-3 py-2 border border-slate-300 rounded-sm text-sm" data-testid="pd-hindrance-impact-input" />
            <input type="date" value={form.expected_resolution_date} onChange={e => setForm({ ...form, expected_resolution_date: e.target.value })} className="px-3 py-2 border border-slate-300 rounded-sm text-sm" />
          </div>
          <div className="flex gap-2 justify-end">
            <button onClick={() => setShowForm(false)} className="px-3 py-1.5 text-xs font-display font-semibold text-slate-500">Cancel</button>
            <button onClick={submit} className="px-4 py-1.5 text-xs font-display font-bold rounded-sm bg-[#3b4872] text-white hover:bg-[#2f3a5e]" data-testid="pd-hindrance-submit-btn">Save</button>
          </div>
        </div>
      )}
      {loading ? <div className="text-sm text-slate-400 italic">Loading…</div> :
        rows.length === 0 ? <EmptyState icon={AlertTriangle} label="No hindrances logged" hint="Log a delay or blocker via Log Hindrance" /> :
        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
          <table className="w-full text-xs"><thead className="bg-slate-50">
            <tr><th className="text-left px-3 py-2">Title</th><th className="text-left px-3 py-2">Responsibility</th><th className="text-right px-3 py-2">Impact</th><th className="text-left px-3 py-2">Raised</th><th className="text-left px-3 py-2">Expected</th><th className="text-left px-3 py-2">Status</th><th className="px-3 py-2"></th></tr>
          </thead><tbody>{rows.map(h => (
            <tr key={h.id} className="border-t border-slate-100 hover:bg-slate-50">
              <td className="px-3 py-2">
                <div className="font-display font-semibold text-[12px] text-[#0F172A]">{h.title}</div>
                {h.description && <div className="text-[11px] text-slate-500 truncate max-w-[280px]">{h.description}</div>}
              </td>
              <td className="px-3 py-2"><span className={`tag border ${RESP_TONE[h.responsibility] || RESP_TONE.Other}`}>{h.responsibility}</span></td>
              <td className="px-3 py-2 text-right font-display font-bold tabular-nums">{(h.impact_days || 0).toFixed(1)}d</td>
              <td className="px-3 py-2 text-slate-600">{fmtDate(h.raised_at)}</td>
              <td className="px-3 py-2 text-slate-600">{fmtDate(h.expected_resolution_date)}</td>
              <td className="px-3 py-2"><span className={`tag border ${STATUS_TONE[h.status] || STATUS_TONE.open}`}>{(h.status || 'open').replace('_', ' ')}</span></td>
              <td className="px-3 py-2 text-right">
                {(h.status === 'open' || h.status === 'in_progress') && (
                  <button onClick={() => resolve(h)} className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-700 hover:underline" data-testid={`pd-hindrance-resolve-${h.id}`}>
                    <CheckCircle2 size={13} /> Resolve
                  </button>
                )}
              </td>
            </tr>))}</tbody></table>
        </div>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// 7. Weather — read + quick-log
// ─────────────────────────────────────────────────────────────
export function WeatherTab({ project }) {
  const { data, loading, reload } = useTabData(project.id, 'weather');
  const [showForm, setShowForm] = useState(false);
  const items = data?.items || []; const s = data?.stats || {};
  return (
    <div className="space-y-4" data-testid="pd-weather">
      <SectionHeader title="Weather Log" onRefresh={reload}
        extra={<button onClick={()=>setShowForm(true)} className="px-2 py-1 text-xs bg-[#3b4872] text-white hover:bg-[#2d3856] rounded-sm flex items-center gap-1 hover:bg-[#3b4872]" data-testid="pd-weather-add"><Plus size={12} /> Quick Log</button>} />
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <StatCard label="Total Entries" value={s.total ?? 0} />
        <StatCard label="Rainy Days" value={s.rainy_days ?? 0} />
        <StatCard label="Impact Hours" value={s.impact_hours_total ?? 0} />
      </div>
      {showForm && <QuickLogModal title="Log Weather" projectId={project.id} slug="weather" onClose={()=>setShowForm(false)} onSaved={()=>{setShowForm(false); reload();}}
        fields={[
          {key:'date', label:'Date', type:'date'},
          {key:'condition', label:'Condition', type:'select', required:true, options:['Clear','Cloudy','Light Rain','Heavy Rain','Storm','Fog','Hot','Cold']},
          {key:'min_temp_c', label:'Min Temp °C', type:'number'},
          {key:'max_temp_c', label:'Max Temp °C', type:'number'},
          {key:'rainfall_mm', label:'Rainfall mm', type:'number'},
          {key:'wind_kmph', label:'Wind kmph', type:'number'},
          {key:'impact_hours', label:'Impact Hours', type:'number', step:0.5},
          {key:'notes', label:'Notes', type:'textarea'},
        ]} />}
      {loading ? <div className="text-sm text-slate-400 italic">Loading…</div> :
        items.length === 0 ? <EmptyState icon={CloudRain} label="No weather entries" hint="Log site weather via Quick Log" /> :
        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
          <table className="w-full text-xs"><thead className="bg-slate-50">
            <tr><th className="text-left px-3 py-2">Date</th><th className="text-left px-3 py-2">Condition</th><th className="text-right px-3 py-2">Min °C</th><th className="text-right px-3 py-2">Max °C</th><th className="text-right px-3 py-2">Rain mm</th><th className="text-right px-3 py-2">Impact h</th></tr>
          </thead><tbody>{items.map((w,i) => (
            <tr key={w.id || i} className="border-t border-slate-100">
              <td className="px-3 py-2">{w.date}</td>
              <td className="px-3 py-2">{w.condition}</td>
              <td className="px-3 py-2 text-right tabular-nums">{w.min_temp_c ?? '—'}</td>
              <td className="px-3 py-2 text-right tabular-nums">{w.max_temp_c ?? '—'}</td>
              <td className="px-3 py-2 text-right tabular-nums">{w.rainfall_mm ?? 0}</td>
              <td className="px-3 py-2 text-right tabular-nums">{w.impact_hours ?? 0}</td>
            </tr>))}</tbody></table>
        </div>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// 8. Safety — read + quick-log
// ─────────────────────────────────────────────────────────────
export function SafetyTab({ project }) {
  const { data, loading, reload } = useTabData(project.id, 'safety');
  const [showForm, setShowForm] = useState(false);
  const items = data?.items || []; const s = data?.stats || {};
  return (
    <div className="space-y-4" data-testid="pd-safety">
      <SectionHeader title="Safety Observations & Incidents" onRefresh={reload}
        extra={<button onClick={()=>setShowForm(true)} className="px-2 py-1 text-xs bg-[#3b4872] text-white hover:bg-[#2d3856] rounded-sm flex items-center gap-1 hover:bg-[#3b4872]" data-testid="pd-safety-add"><Plus size={12} /> Quick Log</button>} />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Open NCRs" value={s.ncrs_open ?? 0} />
        <StatCard label="HSE Incidents" value={s.incidents_total ?? 0} />
        <StatCard label="Observations (30d)" value={s.observations_30d ?? 0} />
        <StatCard label="Critical" value={s.critical_count ?? 0} />
      </div>
      {showForm && <QuickLogModal title="Log Safety Observation" projectId={project.id} slug="safety" onClose={()=>setShowForm(false)} onSaved={()=>{setShowForm(false); reload();}}
        fields={[
          {key:'date', label:'Date', type:'date'},
          {key:'type', label:'Type', type:'select', required:true, options:['Observation','Near Miss','First Aid','Lost Time','Property Damage']},
          {key:'severity', label:'Severity', type:'select', options:['Low','Medium','High','Critical']},
          {key:'location', label:'Location'},
          {key:'person_involved', label:'Person Involved'},
          {key:'description', label:'Description', type:'textarea', required:true},
          {key:'action_taken', label:'Action Taken', type:'textarea'},
        ]} />}
      {loading ? <div className="text-sm text-slate-400 italic">Loading…</div> :
        items.length === 0 ? <EmptyState icon={Shield} label="No safety records" hint="Capture an observation via Quick Log" /> :
        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
          <table className="w-full text-xs"><thead className="bg-slate-50">
            <tr><th className="text-left px-3 py-2">Date</th><th className="text-left px-3 py-2">Type</th><th className="text-left px-3 py-2">Severity</th><th className="text-left px-3 py-2">Description</th><th className="text-left px-3 py-2">Location</th><th className="text-left px-3 py-2">Source</th></tr>
          </thead><tbody>{items.map((it,i) => (
            <tr key={it.id || i} className="border-t border-slate-100">
              <td className="px-3 py-2">{(it.date || it.created_at || '').slice(0,10)}</td>
              <td className="px-3 py-2">{it.type || it._source}</td>
              <td className="px-3 py-2"><span className={`px-1.5 py-0.5 text-[10px] rounded-sm ${it.severity==='Critical'?'bg-red-50 text-red-700':it.severity==='High'?'bg-orange-50 text-orange-700':'bg-slate-100'}`}>{it.severity}</span></td>
              <td className="px-3 py-2 max-w-[400px]">{it.description || it.title || '—'}</td>
              <td className="px-3 py-2">{it.location || '—'}</td>
              <td className="px-3 py-2 text-[10px] text-slate-400">{it._source}</td>
            </tr>))}</tbody></table>
        </div>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// 9. RFI
// ─────────────────────────────────────────────────────────────
export function RFITab({ project }) {
  const { data, loading, reload } = useTabData(project.id, 'rfi');
  const items = data?.items || []; const s = data?.stats || {};
  return (
    <div className="space-y-4" data-testid="pd-rfi">
      <SectionHeader title="RFI (Requests for Information)" onRefresh={reload}
        extra={<Link to="/app/engineering" className="text-xs text-[#3b4872] hover:underline">Open Engineering →</Link>} />
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatCard label="Total" value={s.total ?? 0} />
        <StatCard label="Open" value={s.open ?? 0} />
        <StatCard label="Closed" value={s.closed ?? 0} />
        <StatCard label="Overdue" value={s.overdue ?? 0} />
        <StatCard label="Critical" value={s.critical ?? 0} />
      </div>
      {loading ? <div className="text-sm text-slate-400 italic">Loading…</div> :
        items.length === 0 ? <EmptyState icon={HelpCircle} label="No RFIs raised" hint="Create one from Engineering → RFI → New" /> :
        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
          <table className="w-full text-xs"><thead className="bg-slate-50">
            <tr><th className="text-left px-3 py-2">RFI No</th><th className="text-left px-3 py-2">Subject</th><th className="text-left px-3 py-2">Severity</th><th className="text-left px-3 py-2">Status</th><th className="text-left px-3 py-2">Raised</th><th className="text-left px-3 py-2">Due</th></tr>
          </thead><tbody>{items.map((r,i) => (
            <tr key={r.id || i} className="border-t border-slate-100">
              <td className="px-3 py-2 font-mono text-[11px]">{r.rfi_no || r.id?.slice(0,8)}</td>
              <td className="px-3 py-2 max-w-[400px] truncate">{r.subject || r.title}</td>
              <td className="px-3 py-2"><span className={`px-1.5 py-0.5 text-[10px] rounded-sm ${r.severity==='Critical'?'bg-red-50 text-red-700':r.severity==='High'?'bg-orange-50 text-orange-700':'bg-slate-100'}`}>{r.severity}</span></td>
              <td className="px-3 py-2"><span className="px-1.5 py-0.5 text-[10px] rounded-sm bg-slate-100">{r.status}</span></td>
              <td className="px-3 py-2 text-slate-500">{(r.created_at || '').slice(0,10)}</td>
              <td className="px-3 py-2 text-slate-500">{(r.response_due_date || '').slice(0,10)}</td>
            </tr>))}</tbody></table>
        </div>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Quick-log modal (shared by Hindrance / Weather / Safety)
// ─────────────────────────────────────────────────────────────
function QuickLogModal({ title, projectId, slug, fields, onClose, onSaved }) {
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    const required = fields.filter(f => f.required);
    for (const r of required) {
      if (!form[r.key]) { toast.error(`${r.label} is required`); return; }
    }
    setBusy(true);
    try {
      await apiPost(`/projects/${projectId}/dashboard/${slug}`, form);
      toast.success('Logged successfully');
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save');
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid={`pd-${slug}-modal`}>
      <div className="bg-white border border-slate-200 rounded-sm w-full max-w-lg shadow-2xl">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
          <h3 className="font-display font-bold">{title}</h3>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded-sm" data-testid={`pd-${slug}-modal-close`}><X size={16} /></button>
        </div>
        <div className="p-4 grid grid-cols-2 gap-3 max-h-[70vh] overflow-y-auto">
          {fields.map(f => (
            <div key={f.key} className={f.type === 'textarea' ? 'col-span-2' : ''}>
              <label className="text-[10px] uppercase tracking-widest font-display text-slate-500 mb-1 block">{f.label}{f.required && <span className="text-red-500"> *</span>}</label>
              {f.type === 'select' ? (
                <select className="w-full px-2 py-1.5 border border-slate-300 rounded-sm text-sm" value={form[f.key] || ''} onChange={e=>setForm({...form,[f.key]:e.target.value})} data-testid={`pd-${slug}-${f.key}`}>
                  <option value="">— select —</option>
                  {f.options.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              ) : f.type === 'textarea' ? (
                <textarea className="w-full px-2 py-1.5 border border-slate-300 rounded-sm text-sm" rows={3} value={form[f.key] || ''} onChange={e=>setForm({...form,[f.key]:e.target.value})} data-testid={`pd-${slug}-${f.key}`} />
              ) : (
                <input type={f.type || 'text'} step={f.step} className="w-full px-2 py-1.5 border border-slate-300 rounded-sm text-sm" value={form[f.key] || ''} onChange={e=>setForm({...form,[f.key]:f.type==='number' ? Number(e.target.value) : e.target.value})} data-testid={`pd-${slug}-${f.key}`} />
              )}
            </div>
          ))}
        </div>
        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-slate-200">
          <button onClick={onClose} className="px-3 py-1.5 text-xs border border-slate-300 rounded-sm hover:bg-slate-50">Cancel</button>
          <button onClick={submit} disabled={busy} className="px-4 py-1.5 text-xs bg-[#3b4872] text-white hover:bg-[#2d3856] rounded-sm hover:bg-[#3b4872] disabled:opacity-50 font-display font-bold uppercase tracking-wider" data-testid={`pd-${slug}-submit`}>{busy ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}
